"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronsDownUp, FolderTree, Loader2, Plus, Search, Settings2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  buildSearchIndex,
  searchCategories,
  SEARCH_LIMIT,
} from "@/lib/categories/category-search";
import {
  ancestorsOf,
  categoryStatuses,
  collectBranchIds,
  countNodes,
  findNode,
} from "@/lib/categories/tree-model";
import { CategorySearchResults } from "./category-search-results";
import { CategoryTree } from "./category-tree";
import type { TreeRowNode } from "./category-tree";

/** Below this, the whole tree opens on load — a small catalogue behaves as before. */
export const AUTO_EXPAND_MAX = 60;
/** At or above this, rows are windowed; below it every row renders so Cmd+F works. */
export const VIRTUALIZE_MIN = 300;
export const NAV_MIN = 280;
export const NAV_MAX = 560;
export const NAV_DEFAULT = 340;
export const NAV_WIDTH_KEY = "youzu.categories.navWidth";

interface CategoryNavigatorProps<T extends TreeRowNode<T>> {
  nodes: T[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  draftIds: ReadonlySet<string>;
  readOnly?: boolean;
  onSelect: (node: T) => void;
  onCreateRoot: () => void;
  renderRowMenu: (node: T) => ReactNode;
  /** Label on the "add a top-level entry" button. */
  createLabel?: string;
  /**
   * The pinned entry above the tree. Omitted by trees that have no
   * catalogue-wide scope — the template tree does not.
   */
  pinnedEntry?: {
    label: string;
    hint: string;
    selected: boolean;
    onSelect: () => void;
  };
}

function readStoredWidth(): number {
  try {
    const raw = window.localStorage.getItem(NAV_WIDTH_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
    if (Number.isFinite(parsed)) {
      return Math.min(NAV_MAX, Math.max(NAV_MIN, parsed));
    }
  } catch {
    /* storage unavailable — fall through to the default */
  }
  return NAV_DEFAULT;
}

export function CategoryNavigator<T extends TreeRowNode<T>>({
  nodes,
  loading,
  error,
  selectedId,
  draftIds,
  readOnly = false,
  onSelect,
  onCreateRoot,
  renderRowMenu,
  createLabel = "New",
  pinnedEntry,
}: CategoryNavigatorProps<T>) {
  const statuses = useMemo(() => categoryStatuses(nodes), [nodes]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [width, setWidth] = useState(NAV_DEFAULT);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const total = useMemo(() => countNodes(nodes), [nodes]);
  const index = useMemo(() => buildSearchIndex(nodes), [nodes]);
  const results = useMemo(
    () => (query.trim() ? searchCategories(index, query) : []),
    [index, query],
  );

  useEffect(() => setWidth(readStoredWidth()), []);

  // A small catalogue opens fully; a large one starts collapsed and is searched.
  useEffect(() => {
    if (!nodes.length) return;
    if (countNodes(nodes) <= AUTO_EXPAND_MAX) {
      const all = new Set<string>();
      const walk = (list: T[]) =>
        list.forEach((n) => {
          if (n.children.length) all.add(n.id);
          walk(n.children);
        });
      walk(nodes);
      setExpanded(all);
    }
  }, [nodes]);

  // "/" focuses search from anywhere that is not already a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandBranch = useCallback((node: T) => {
    setExpanded((prev) => new Set([...prev, ...collectBranchIds(node)]));
  }, []);

  const revealAndSelect = useCallback(
    (node: T) => {
      setExpanded(
        (prev) => new Set([...prev, ...ancestorsOf(nodes, node.id).map((a) => a.id)]),
      );
      onSelect(node);
    },
    [nodes, onSelect],
  );

  const selectResult = useCallback(
    (id: string) => {
      const node = findNode(nodes, id);
      if (node) revealAndSelect(node);
    },
    [nodes, revealAndSelect],
  );

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setQuery("");
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectResult(results[Math.max(0, activeIndex)].id);
    }
  };

  const applyWidth = useCallback((px: number, persist: boolean) => {
    const clamped = Math.round(Math.min(NAV_MAX, Math.max(NAV_MIN, px)));
    setWidth(clamped);
    if (persist) {
      try {
        window.localStorage.setItem(NAV_WIDTH_KEY, String(clamped));
      } catch {
        /* storage unavailable — the width simply is not remembered */
      }
    }
  }, []);

  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const left = panelRef.current?.getBoundingClientRect().left ?? 0;
    const onMove = (ev: PointerEvent) => applyWidth(ev.clientX - left, false);
    const onUp = (ev: PointerEvent) => {
      applyWidth(ev.clientX - left, true);
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
  };

  const onResizeKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 40 : 10;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      applyWidth(width - step, true);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      applyWidth(width + step, true);
    } else if (e.key === "Home") {
      e.preventDefault();
      applyWidth(NAV_DEFAULT, true);
    }
  };

  const searching = query.trim().length > 0;

  return (
    <div className="relative flex min-h-0" ref={panelRef}>
      <div
        className="flex min-h-0 flex-col border-r bg-muted/30"
        style={{ width }}
      >
        <div className="flex items-center justify-between gap-2 px-3 pb-2.5 pt-3">
          <div className="flex items-baseline gap-2 text-sm font-semibold">
            <FolderTree className="h-4 w-4 self-center" />
            Categories
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {total.toLocaleString("en-GB")}
            </span>
          </div>
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              title="Collapse all"
              aria-label="Collapse all"
              onClick={() => setExpanded(new Set())}
            >
              <ChevronsDownUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={onCreateRoot}
              disabled={readOnly}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> {createLabel}
            </Button>
          </div>
        </div>

        <div className="relative px-3 pb-2.5">
          <Search className="pointer-events-none absolute left-[22px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onSearchKeyDown}
            placeholder={
              total > VIRTUALIZE_MIN
                ? `Search ${total.toLocaleString("en-GB")} categories`
                : "Filter categories"
            }
            aria-label="Search categories and paths"
            className="h-8 pl-8 pr-8 text-[13px]"
          />
          {searching && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted"
              onClick={() => setQuery("")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {pinnedEntry && (
          <button
            type="button"
            aria-current={pinnedEntry.selected}
            title={pinnedEntry.hint}
            className={cn(
              "mx-3 mb-1.5 flex items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-left text-[13px]",
              pinnedEntry.selected
                ? "border-solid border-pink-200 bg-pink-50 font-medium text-pink-700"
                : "hover:bg-muted",
            )}
            onClick={pinnedEntry.onSelect}
          >
            <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
            {pinnedEntry.label}
          </button>
        )}

        {loading ? (
          <div className="flex flex-1 items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <p className="px-3 py-4 text-sm text-destructive">{error}</p>
        ) : !nodes.length ? (
          <p className="px-3 py-4 text-sm italic text-muted-foreground">
            No categories yet. Create one to get started.
          </p>
        ) : searching ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <CategorySearchResults
              statuses={statuses}
              results={results}
              selectedId={selectedId}
              activeIndex={activeIndex}
              truncated={results.length >= SEARCH_LIMIT}
              onSelect={selectResult}
            />
          </div>
        ) : (
          <CategoryTree
            statuses={statuses}
            nodes={nodes}
            selectedId={selectedId}
            expanded={expanded}
            draftIds={draftIds}
            onToggle={toggle}
            onExpandBranch={expandBranch}
            onSelect={revealAndSelect}
            renderRowMenu={renderRowMenu}
            virtualize={total >= VIRTUALIZE_MIN}
          />
        )}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the category panel"
        aria-valuenow={width}
        aria-valuemin={NAV_MIN}
        aria-valuemax={NAV_MAX}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        className="group absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize touch-none"
        onPointerDown={onResizePointerDown}
        onKeyDown={onResizeKeyDown}
        onDoubleClick={() => applyWidth(NAV_DEFAULT, true)}
      >
        <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-pink-500 group-focus-visible:bg-pink-500" />
      </div>
    </div>
  );
}
